// Client import from a spreadsheet (CSV / Excel) — for a new advisor bringing an existing client list (setup wizard,
// Data & safety). The flow is the same as every AVRASTRO import: file → explicit column mapping (a first guess from the
// header row, the advisor confirms) → preview with a class per row → one transaction.
//
// Duplicates are never merged silently: a row whose phone, e-mail or name (Giannis = Ioannis = Γιάννης, same surname,
// no conflicting birth date) matches an existing person is shown as «already in AVRASTRO» and is skipped unless the
// advisor ticks it; a row repeated inside the file is imported once. Rows without a name are not imported.
import { DomainError, type Ctx } from './cmd.ts';
import { mergeChangesets, type Changeset, type WorkspaceView } from './changeset.ts';
import { createPerson } from './services.ts';
import { normalizeDay, type Day } from './dates.ts';
import { phoneMatchKey, searchKey } from './rules.ts';
import { sameName } from './names.ts';
import type { Person, PersonStage } from './schema.ts';

export const CLIENT_IMPORT_FIELDS = ['displayName', 'firstName', 'lastName', 'phone', 'email', 'birthDate', 'address', 'city', 'occupation', 'clientSince', 'notes'] as const;
export type ClientImportField = typeof CLIENT_IMPORT_FIELDS[number];
export type ClientMapping = Partial<Record<ClientImportField, number>>;

// header hints compare against searchKey(header): lower case, no accents
const HINTS: Record<ClientImportField, RegExp> = {
  displayName: /^(full ?name|name|client|customer|ονοματεπωνυμο|πελατης|ονομα και επωνυμο|ф\.?и\.?о\.?|клиент|имя и фамилия)$/,
  firstName: /^(first ?name|given ?name|forename|ονομα|имя)$/,
  lastName: /^(last ?name|surname|family ?name|επωνυμο|фамилия)$/,
  phone: /phone|mobile|tel|κινητο|τηλεφωνο|τηλ|телефон|мобильный/,
  email: /e-?mail|ηλεκτρονικο ταχυδρομειο|почта/,
  birthDate: /birth|dob|γεννηση|ημερομηνια γεννησης|рождени/,
  address: /address|διευθυνση|street|οδος|адрес/,
  city: /city|town|πολη|επαρχια|город/,
  occupation: /occupation|profession|job|επαγγελμα|профессия|должность/,
  clientSince: /client since|customer since|since|πελατης απο|απο το|клиент с/,
  notes: /notes?|comments?|σημειωσ|σχολι|примечан|комментар/,
};
/** a first guess of the mapping from the header row (the advisor confirms or changes it) */
export function guessClientMapping(header: readonly string[]): ClientMapping {
  const out: ClientMapping = {};
  const used = new Set<number>();
  // the specific fields first, so «Όνομα» is not taken as the full name when «Επώνυμο» exists
  const order: ClientImportField[] = ['firstName', 'lastName', 'displayName', 'birthDate', 'clientSince', 'email', 'phone', 'address', 'city', 'occupation', 'notes'];
  for (const f of order) {
    const i = header.findIndex((h, idx) => !used.has(idx) && HINTS[f].test(searchKey(h)));
    if (i >= 0) { out[f] = i; used.add(i); }
  }
  // a lone «Όνομα»/«Name» column with no surname column is the full name
  if (out.firstName !== undefined && out.lastName === undefined && out.displayName === undefined) { out.displayName = out.firstName; delete out.firstName; }
  return out;
}

/** Excel stores dates as day numbers (1900 system); a plain number in a date column is read that way.
 *  A bare 4-digit year 1900–2100 («2000») is NOT a day number (it would become 1905-06-22) nor 1 January: it is an
 *  incomplete date → null, so the preview flags the row (birth_date / client_since problem) instead of guessing. */
export function spreadsheetDay(v: string): Day | null {
  const s = String(v ?? '').trim();
  if (/^\d{4}(\.0+)?$/.test(s) && +s >= 1900 && +s <= 2100) return null;
  if (/^\d{4,5}(\.\d+)?$/.test(s)) {
    const n = Math.floor(Number(s));
    if (n > 60 && n < 80000) return new Date(Date.UTC(1899, 11, 30) + n * 86400000).toISOString().slice(0, 10);
  }
  return normalizeDay(s);
}

export type ClientRowClass = 'new' | 'existing' | 'repeat' | 'invalid';
export interface ClientRowPreview {
  line: number;
  values: Partial<Record<ClientImportField, string>>;
  name: string;
  phone: string | null; email: string | null; birthDate: Day | null; clientSince: Day | null;
  kind: ClientRowClass;
  /** existing person this row matches (kind 'existing'), and on what */
  matchId?: string; matchOn?: 'phone' | 'email' | 'name';
  problems: ('name' | 'birth_date' | 'client_since' | 'email')[];
}
export interface ClientImportPreview { rows: ClientRowPreview[]; counts: Record<ClientRowClass, number> }

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
export function previewClientImport(v: WorkspaceView, grid: readonly (readonly string[])[], mapping: ClientMapping, today: Day, opts: { hasHeader?: boolean } = {}): ClientImportPreview {
  const people = v.rows('persons').filter((p) => !p.archivedAt);
  const byPhone = new Map<string, Person>(), byEmail = new Map<string, Person>();
  for (const p of people) {
    for (const ph of [p.primaryPhone, ...(p.phones ?? []).map((x) => x.value)]) { const k = phoneMatchKey(ph); if (k.length >= 8 && !byPhone.has(k)) byPhone.set(k, p); }
    for (const em of [p.primaryEmail, ...(p.emails ?? []).map((x) => x.value)]) { const k = String(em ?? '').trim().toLowerCase(); if (k.includes('@') && !byEmail.has(k)) byEmail.set(k, p); }
  }
  const seen = new Set<string>();
  const rows: ClientRowPreview[] = [];
  const start = opts.hasHeader === false ? 0 : 1;
  for (let i = start; i < grid.length; i++) {
    const r = grid[i]!;
    if (!r.some((c) => String(c ?? '').trim())) continue; // blank line
    const values: ClientRowPreview['values'] = {};
    for (const f of CLIENT_IMPORT_FIELDS) { const idx = mapping[f]; if (idx !== undefined && idx >= 0) { const val = String(r[idx] ?? '').trim(); if (val) values[f] = val; } }
    const name = (values.displayName || [values.firstName, values.lastName].filter(Boolean).join(' ')).replace(/\s+/g, ' ').trim();
    const problems: ClientRowPreview['problems'] = [];
    if (!name) problems.push('name');
    const birthDate = values.birthDate ? spreadsheetDay(values.birthDate) : null;
    if (values.birthDate && (!birthDate || birthDate > today || birthDate < '1900-01-01')) problems.push('birth_date');
    const clientSince = values.clientSince ? spreadsheetDay(values.clientSince) : null;
    if (values.clientSince && (!clientSince || clientSince > today)) problems.push('client_since');
    const email = values.email ? values.email.toLowerCase() : null;
    if (email && !EMAIL.test(email)) problems.push('email');
    const phone = values.phone ? values.phone.replace(/[^\d+ ]/g, '').trim() || null : null;
    const row: ClientRowPreview = { line: i + 1, values, name, phone, email: email && EMAIL.test(email) ? email : null, birthDate: problems.includes('birth_date') ? null : birthDate, clientSince: problems.includes('client_since') ? null : clientSince, kind: 'new', problems };
    if (!name) { row.kind = 'invalid'; rows.push(row); continue; }
    // matching an existing person: phone or e-mail; or the same name (canonical first name + surname) without a different birth date
    const pk = phoneMatchKey(phone), ek = row.email ?? '';
    const hit = (pk.length >= 8 && byPhone.get(pk)) ? { p: byPhone.get(pk)!, on: 'phone' as const }
      : (ek && byEmail.get(ek)) ? { p: byEmail.get(ek)!, on: 'email' as const }
      : (() => { const p = people.find((x) => sameName(x.displayName, name) && !(x.birthDate && row.birthDate && x.birthDate !== row.birthDate)); return p ? { p, on: 'name' as const } : null; })();
    if (hit) { row.kind = 'existing'; row.matchId = hit.p.id; row.matchOn = hit.on; rows.push(row); continue; }
    const key = pk.length >= 8 ? `p:${pk}` : ek ? `e:${ek}` : `n:${searchKey(name)}|${row.birthDate ?? ''}`;
    if (seen.has(key)) { row.kind = 'repeat'; rows.push(row); continue; }
    seen.add(key);
    rows.push(row);
  }
  const counts: Record<ClientRowClass, number> = { new: 0, existing: 0, repeat: 0, invalid: 0 };
  for (const r of rows) counts[r.kind]++;
  return { rows, counts };
}

export interface ClientImportOptions { stage?: Extract<PersonStage, 'client' | 'prospect'>; source?: string | null; /** lines of 'existing' rows the advisor chose to add anyway */ includeExisting?: readonly number[] }
/** Adds the 'new' rows (and the existing-match rows the advisor ticked) as people, in one transaction. */
export function importClients(v: WorkspaceView, preview: ClientImportPreview, ctx: Ctx, opts: ClientImportOptions = {}): Changeset {
  const stage = opts.stage ?? 'client';
  if (stage !== 'client' && stage !== 'prospect') throw new DomainError('client_import.stage');
  const extra = new Set(opts.includeExisting ?? []);
  const take = preview.rows.filter((r) => r.kind === 'new' || (r.kind === 'existing' && extra.has(r.line)));
  if (!take.length) throw new DomainError('client_import.empty');
  if (take.length > 5000) throw new DomainError('client_import.too_many');
  const parts = take.map((r) => createPerson(v, {
    displayName: r.values.displayName ? r.name : undefined, firstName: r.values.displayName ? undefined : r.values.firstName, lastName: r.values.displayName ? undefined : r.values.lastName,
    stage, primaryPhone: r.phone ?? undefined, primaryEmail: r.email ?? undefined, birthDate: r.birthDate, address: r.values.address, city: r.values.city,
    occupation: r.values.occupation, notes: r.values.notes, source: opts.source ?? 'import', clientSince: stage === 'client' ? r.clientSince : null,
  }, ctx));
  const cs = mergeChangesets('person.import', ...parts);
  return { ...cs, entity: 'persons', summary: String(take.length) };
}
